import * as React from 'react';
import { NS } from '@/common/namespaces';
import { blazorAlert, blazorOpenPersistentAlert, blazorClosePersistentAlert, blazorShowToast, blazorHideToast, iterateReadableStreamAsString } from "@/common/utilities";
import { IllustratorArtType } from "@/common/enums";
import { SubscriberService, OnUpdateEvent, createStaticServiceInstance, createUseZenAutoArtService } from "./base-subscriber-service";
import { blazorServiceInstance } from './blazor-service';
import { filesServiceInstance } from './files-service';
import { svgEditServiceInstance } from './svg-edit-service';
import { zenDataServiceInstance } from './zen-data-service';
import { metadataServiceInstance } from './metadata-service';
import { registerCopyEventListeners } from './copy-event-handlers';

const LOAD_ARTWORK_RETRY_INTERVAL = 5000;
const UPDATED_FILE_CHECK_INTERVAL = 10000;

enum DeliverableType {
    Barcode = "barcode",
    Braille = "braille",
    BriefDeliverables = "briefdeliverables",
    Panels = "panels",
    Copy = "copy"
}

interface IArtworkInfo {
    metaData?: string | null;
    MetaData?: string | null;
}

interface IAutomationCommandMessage {
    method?: string;
    args?: Record<string, unknown>;
}

// Extend window to register custom event type
declare global {
    interface WindowEventMap {
        'signalr-message': CustomEvent<{ method: string; args: Record<string, unknown> }>;
    }
}

/**
 * Represents an automation message received from SignalR.
 */
interface AutomationSignalRMessage {
    method: string;
    args: Record<string, unknown>;
}

export default class DesignerService extends SubscriberService<DesignerService> {
    public static Get(comp: React.Component, onUpdate?: OnUpdateEvent) {
        return designerServiceInstance._RegisterComponent(comp, onUpdate);
    }

    public typeScriptDefinitions: string;

    private dpi: number = 96;
    private hasStartedLoadingInitialFile: boolean = false;
    private hasStartedAutomationSubscriptions: boolean = false;

    private checkFileChangesTimeout: ReturnType<typeof setTimeout> = null;
    private initialFileLastUpdated: Date = null;
    private metadataFileLastUpdated: Date = null;
    private lastOpenedReloadFileToastId: string | null = null;


    private supressLoadingSpinner: boolean = false;
    #isLoading: boolean;
    public get isLoading(): boolean {
        return this.#isLoading;
    }

    private set isLoading(newState: boolean) {
        this.#isLoading = newState;
        if(newState && !this.supressLoadingSpinner) {
            blazorServiceInstance.PushGlobalLoadingState("DesignerService");
        } else {
            blazorServiceInstance.PopGlobalLoadingState("DesignerService");
        }
    }

    constructor() {
        super();

        window.addEventListener("zaa-editor-ready", () => {
            this.isLoading = true; // Blazor is live now, so setting this to true won't blow things up.
             this.RefreshAllData();
         });

         // Register copy event listeners for new notification-based architecture
         registerCopyEventListeners();

         window.addEventListener("svgcontent-changed", (ev) => {
            if (zenDataServiceInstance.GetDesignerMode() === "artwork") {
                ev.detail.setAttribute("zaa:version", "latest"); // Force latest rendering at all costs.
            }
        });

        // Listen for SignalR messages dispatched by the C# AutomationSignalRService
        window.addEventListener("signalr-message", (ev: CustomEvent<{ method: string; args: Record<string, unknown> }>) => {
            const message = ev.detail;
            
            // Handle automation messages that are relevant to the current artwork.
            if (message.method === "ZenChangeNotification"
                && typeof message.args === "object"
                && typeof message.args.TRNumber === "string"
                && message.args.TRNumber.toUpperCase() === `TR${zenDataServiceInstance.GetArtworkTrNumber()}`
                && typeof message.args.InstructionName === "string"
                && message.args.InstructionName.startsWith("ELEMENT_REQUEST:")
            ) {
                const deliverableType = message.args.InstructionName.substring("ELEMENT_REQUEST:".length).toLowerCase().trim() as DeliverableType;
                
                if (deliverableType == DeliverableType.Copy) {
            // Copy handling is now done by C# - just log for debugging
            console.log("[DesignerService] Received Copy automation command (handled by C#)", message);
                } else {
                    this.HandleDeliverableUpdate(deliverableType);
                }
            }
        });


        zenDataServiceInstance.AddOnDataChangedCallback(() => {
            if (this.hasStartedLoadingInitialFile) {
                return;
            }

            this.hasStartedLoadingInitialFile = true;
            this.LoadArtworkFile();
        });
    }

    public async RefreshAllData() {
        Promise.all([
            this.RefreshTypescriptDefinitions()
        ]).then(() => {
            console.log("Designer Service - Data Refreshed");
        });
    }

    public async RefreshTypescriptDefinitions() {
        const response = await fetch(`/api/designer/ArtScriptDefinitions`);

        if (!response.ok) {
            console.error(`Could not fetch TypeScript definitions. A HTTP ${response.status} (${response.statusText}) error occured. Making another attempt in 2 seconds.`, response);
            setTimeout(() => { this.RefreshTypescriptDefinitions() }, 2000);
            return;
        }

        const defs = await response.text();

        if (!defs || defs.length === 0) {
            console.error("Could not fetch TypeScript definitions. No data was returned from repsonse. Making another attempt in 2 seconds.", response);
            setTimeout(() => { this.RefreshTypescriptDefinitions() }, 2000);
            return;
        }

        this.typeScriptDefinitions = defs;
    }

    public GetDpi() {
        return this.dpi;
    }

    public SetDpi(newDpi: number) {
        if (newDpi <= 0) {
            newDpi = 96.0;
        }

        this.dpi = newDpi;
        const ev = new CustomEvent("dpi-changed", { detail: newDpi });
        window.dispatchEvent(ev);

        this._UpdateSubscribers();
        return newDpi;
    }

    public async LoadArtworkFile() {
        if(this.lastOpenedReloadFileToastId) {
            blazorHideToast(this.lastOpenedReloadFileToastId);
            this.lastOpenedReloadFileToastId = null;
        }

        if(this.checkFileChangesTimeout) {
            clearTimeout(this.checkFileChangesTimeout);
            this.checkFileChangesTimeout = null;
        }

        if (zenDataServiceInstance.GetDesignerMode() !== "artwork") {
            this.isLoading = false;
            return;
        }

        if (zenDataServiceInstance.GetForceOpen()) {
            await this.InitializeBlankArtworkDocument();
            return;
        }

        const initialFileToLoad = zenDataServiceInstance.GetInitialFileToLoad();
        if (!initialFileToLoad || initialFileToLoad.length == 0) {
            if (zenDataServiceInstance.GetForceOpen()) {
                await this.InitializeBlankArtworkDocument();
                return;
            }

            this.isLoading = false;
            return;
        }

        try {
            this.isLoading = true;

            const artworkFileExists = await filesServiceInstance.GetFileExists(initialFileToLoad);
            if (!artworkFileExists) {
                throw new Error(`File does not exist: ${initialFileToLoad}`);
            }

            const artworkFileResp = await filesServiceInstance.GetFile(initialFileToLoad);

            const artworkFileStream: ReadableStream = await artworkFileResp.stream();
            let svgStr: string = "";
            for await (const chunk of iterateReadableStreamAsString(artworkFileStream)) {
                svgStr += chunk;
            }

            metadataServiceInstance.clearTridentMetadata();
            svgEditServiceInstance.editor.setCurrentFileUrl(initialFileToLoad);
            svgEditServiceInstance.editor.loadSvgString(svgStr);

            this.AddArtworkLayers();

            if (initialFileToLoad.toLowerCase().endsWith(".svg")) {
                const metadataFileToLoad = initialFileToLoad.substring(0, initialFileToLoad.length - 4) + ".json";
                const metadataFileExists = await filesServiceInstance.GetFileExists(metadataFileToLoad);
                if (metadataFileExists) {
                    const metadataResp = await filesServiceInstance.GetFile(metadataFileToLoad);

                    const metadataStream: ReadableStream = await metadataResp.stream();
                    let metadataJson: string = "";
                    for await (const chunk of iterateReadableStreamAsString(metadataStream)) {
                        metadataJson += chunk;
                    }

                    const metadata = JSON.parse(metadataJson);
                    metadataServiceInstance.setTridentMetadata(metadata);
                }
            }

            this.supressLoadingSpinner = false; // We loaded successfully, so we can show the spinner again.
            blazorClosePersistentAlert();

            this.isLoading = false;
            this._UpdateSubscribers();

            for(const deliverableType of [ "barcode", "braille", "briefdeliverables", "panels" ] as DeliverableType[]) {
                await this.HandleDeliverableUpdate(deliverableType);
            }

            this.initialFileLastUpdated = null;
            this.metadataFileLastUpdated = null;
            this.CheckForLoadedFileChanges();
        } catch (err) {
            console.error(err);

            if (zenDataServiceInstance.GetForceOpen()) {
                await this.InitializeBlankArtworkDocument();
                return;
            }

            await blazorOpenPersistentAlert("Missing Artwork File", `
<p><strong>Warning:</strong> The web-based artwork file is missing and could not be fully loaded.</p>
<p>Have you assigned an Illustrator base file and opened it at least once for this job?</p>
<p>If you have, the necessary files could be missing, or GFS may be experiencing issues.</p>
<p>The designer will keep trying to load the file. Once a valid file is found, it will be loaded automatically and this error will disappear.</p>
            `);

            this.supressLoadingSpinner = true; // No need to show the spinner on re-attempts as the error will prevent interaction.
            this.isLoading = false;
            this._UpdateSubscribers();

            setTimeout(() => { this.LoadArtworkFile() }, LOAD_ARTWORK_RETRY_INTERVAL);
        }
    }

    private async InitializeBlankArtworkDocument() {
        const initialFileToLoad = zenDataServiceInstance.GetInitialFileToLoad();
        const [width, height] = svgEditServiceInstance.editor.configObj.curConfig.dimensions;

        metadataServiceInstance.clearTridentMetadata();
        svgEditServiceInstance.canvas.clear();
        svgEditServiceInstance.canvas.setResolution(width, height);

        if (initialFileToLoad && initialFileToLoad.length > 0) {
            svgEditServiceInstance.editor.setCurrentFileUrl(initialFileToLoad);
        }

        svgEditServiceInstance.editor.updateCanvas(true);
        svgEditServiceInstance.editor.zoomImage();
        svgEditServiceInstance.editor.topPanel.updateContextPanel();
        svgEditServiceInstance.canvas.runExtensions("onNewDocument");

        this.supressLoadingSpinner = false;
        blazorClosePersistentAlert();

        this.isLoading = false;
        this._UpdateSubscribers();

        await this.AddArtworkLayers();

        for(const deliverableType of [ "barcode", "braille", "briefdeliverables", "panels" ] as DeliverableType[]) {
            await this.HandleDeliverableUpdate(deliverableType);
        }

        this.initialFileLastUpdated = null;
        this.metadataFileLastUpdated = null;
    }

    public async CheckForLoadedFileChanges() {
        if(this.checkFileChangesTimeout) {
            clearTimeout(this.checkFileChangesTimeout);
            this.checkFileChangesTimeout = null;
        }

        if (zenDataServiceInstance.GetDesignerMode() !== "artwork" || zenDataServiceInstance.GetForceOpen()) {
            return;
        }

        const initialFileToLoad = zenDataServiceInstance.GetInitialFileToLoad();
        if (!initialFileToLoad || initialFileToLoad.length == 0) {
            return;
        }

        const changes: string[] = [];

        const initialFileLastModified = await filesServiceInstance.GetFileLastModified(initialFileToLoad);
        const initialFileLastModifiedTimeStamp = Date.parse(initialFileLastModified);
        if(!Number.isNaN(initialFileLastModifiedTimeStamp)) {
            if(this.initialFileLastUpdated && this.initialFileLastUpdated.getTime() < initialFileLastModifiedTimeStamp) {
                console.log(`Initial file last modified has changed: ${this.initialFileLastUpdated}`);
                changes.push("svg");
            }

            this.initialFileLastUpdated = new Date(initialFileLastModifiedTimeStamp);
        }

        if (initialFileToLoad.toLowerCase().endsWith(".svg")) {
            const metadataFileToLoad = initialFileToLoad.substring(0, initialFileToLoad.length - 4) + ".json";

            const metadataFileLastModified = await filesServiceInstance.GetFileLastModified(metadataFileToLoad);
            const metadataFileLastModifiedTimeStamp = Date.parse(metadataFileLastModified);
            if(!Number.isNaN(metadataFileLastModifiedTimeStamp)) {
                if(this.metadataFileLastUpdated && this.metadataFileLastUpdated.getTime() < metadataFileLastModifiedTimeStamp) {
                    console.log(`Metadata file last modified has changed: ${this.metadataFileLastUpdated}`);
                    changes.push("metadata");
                }

                this.metadataFileLastUpdated = new Date(metadataFileLastModifiedTimeStamp);
            }
        }

        if(changes.length > 0) {
            this.lastOpenedReloadFileToastId = await blazorShowToast("warning", `
<div style="display: flex; justify-content: space-between; align-items: center;">
    <p>
        The artwork file has been updated in GFS outside of this editor. 
        Do you want to reload the file from GFS?
        Any changes made in the designer will be lost!
    </p>
    <button class="btn btn-warning" onclick="_zaa_services.designerService.LoadArtworkFile()">Reload Artwork</button>
</div>`);
        } else {
            // Only need to recheck if there were no changes.
            this.checkFileChangesTimeout = setTimeout(() => { this.CheckForLoadedFileChanges(); }, UPDATED_FILE_CHECK_INTERVAL);
        }
    }

    public async SaveCurrentFile() {
        const url = svgEditServiceInstance.editor.getCurrentFileUrl();
        if (!url || url.trim().length === 0) {
            await blazorAlert("Error", "Cannot save - No file is currently open.");
            return;
        }

        clearTimeout(this.checkFileChangesTimeout);
        const metadata = metadataServiceInstance.getTridentMetadata();

        blazorServiceInstance.PushGlobalLoadingState("SaveArtwork", "Saving artwork...");
        await filesServiceInstance.SaveFile(url, svgEditServiceInstance.canvas.svgCanvasToString());

        // TODO: Investigate. Looks like svgCanvasToString might have weird metadata side effects.
        metadataServiceInstance.setTridentMetadata(metadata);
        this.initialFileLastUpdated = null;
        this.metadataFileLastUpdated = null;

        if (url.toLowerCase().endsWith(".svg")) {
            const metadataUrl = url.substring(0, url.length - 4) + ".json";
            await filesServiceInstance.SaveFile(metadataUrl, metadata);
        }

        blazorServiceInstance.PopGlobalLoadingState("SaveArtwork");

        this.checkFileChangesTimeout = setTimeout(() => { this.CheckForLoadedFileChanges(); }, UPDATED_FILE_CHECK_INTERVAL);
    }

    public async AddArtworkLayers() {
        const artworkTrNumber = zenDataServiceInstance.GetArtworkTrNumber();
        if(!artworkTrNumber || artworkTrNumber.length === 0) {
            // We're not an artwork, so don't do anything.
            return;
        }

        const response = await fetch(`/api/designer/GetLayers/${artworkTrNumber}`);

        if (!response.ok) {
            console.error(response);
            throw new Error(`Error: Could not fetch falyers for job TR${artworkTrNumber}!`);
        }

        const layers = await response.json() as PanelDesigner.ILayer[];
        layers.sort((a, b) => a.position - b.position);

        const drawing = svgEditServiceInstance.canvas.getCurrentDrawing();
        const currentLayerName = drawing.getCurrentLayerName();

        let previousLayer: PanelDesigner.ILayer = null;
        for(const layer of layers) {
            const existingLayer = drawing.getLayerByName(layer.name);
            if(!existingLayer) {
                const newLayerIndex = previousLayer ? drawing.getLayerIndex(previousLayer.name) : -1;
                drawing.createLayer(layer.name);

                if(newLayerIndex >= 0)
                {
                    drawing.setCurrentLayer(layer.name);
                    drawing.setCurrentLayerPosition(newLayerIndex);
                }
            }

            previousLayer = layer;
        }

        drawing.setCurrentLayer(currentLayerName);
        this._UpdateSubscribers();
        svgEditServiceInstance['_UpdateSubscribers']();
    }

    private async HandleDeliverableUpdate(deliverableType: DeliverableType) {
        const artworkTrNumber = zenDataServiceInstance.GetArtworkTrNumber();
        
        if(!artworkTrNumber || artworkTrNumber.length === 0) {
            // We're not an artwork, so don't do anything.
            return;
        }

        const response = await fetch(`/api/designer/GetDeliverables/${deliverableType}/${artworkTrNumber}`);

        if (!response.ok) {
            console.error(response);
            throw new Error(`Error: Could not fetch deliverables of type ${deliverableType}!`);
        }

        const deliverablePacket = await response.json() as PanelDesigner.IDeliverablePacket;

        const deliverablesObj = metadataServiceInstance.getTridentDeliverablesObject(deliverableType);
        for(const newDeliverable of deliverablePacket.Payload.Attributes.Deliverables) {
            for(const deliverableMeta of deliverablesObj) {
                if(deliverableMeta.field_Name === newDeliverable.FIELD_NAME) {
                    if(newDeliverable.DATA_TYPE !== "txt") {
                        console.warn(`TODO: Unsupported deliverable type '${newDeliverable.DATA_TYPE}' for deliverable '${deliverableMeta.field_Name}'`);
                        break;
                    }

                    this.UpdateTextElementByXmlId(deliverableMeta.xmlid, newDeliverable.VALUE, "deliverable");
                    break;
                }
            }
        }
}

    private GetAutomationTRNumber(args: Record<string, unknown>) {
        const trNumber = this.GetAutomationArg(args, "TRNumber");
        if(!trNumber) {
            return null;
        }

        return trNumber.replace(/^TR/i, "");
    }

    private GetAutomationArg(args: Record<string, unknown>, name: string) {
        const value = args[name] ?? args[name.toLowerCase()];
        return typeof value === "string" ? value : null;
    }

     private UpdateTextElementByXmlId(xmlId: string, textValue: string, source: "copy" | "deliverable") {
         let el = this.FindElementByXmlId(xmlId);
         if(!el) {
             console.error(`Could not update ${source}. Could not find element with xmlid '${xmlId}'.`);
             return;
         }

         if(el.tagName.toLowerCase() !== "text") {
             el = el.querySelector("text");
         }

         if(!el) {
             console.error(`Could not update ${source}. Found element does not contain a <text> node.`);
             return;
         }

         const metadata = metadataServiceInstance.getMetadataForElement(el);

         if(!metadata) {
             console.error(`Could not update ${source}. Found element does not have any associated metadata.`);
             return;
         }

         if(metadata.artType !== IllustratorArtType.TextFrameArt) {
             console.error(`Could not update ${source}. Found element has the wrong metadata art type. Expected ${IllustratorArtType.TextFrameArt} (TextFrameArt) but received ${metadata.artType}.`);
             return;
         }

         const charMeta = metadata.character_metadata?.[0];

         if(!charMeta) {
             console.error(`Could not update ${source}. Found element does not have any character metadata.`);
             return;
         }

         const paragraphMeta = metadata.paragraph_metadata?.[0];

         if(!paragraphMeta) {
             console.error(`Could not update ${source}. Found element does not have any paragraph metadata.`);
             return;
         }

         for(const meta of [charMeta, paragraphMeta]) {
             meta.content = textValue;
             meta.index = 0;
             meta.rangeStart = 0;
             meta.rangeEnd = textValue.length;
             meta.rangeSize = textValue.length;
         }

         metadata.character_metadata = [charMeta];
         metadata.paragraph_metadata = [paragraphMeta];

         metadataServiceInstance.fixupTextElementFromMetadata(el);
     }

     private FindElementByXmlId(xmlId: string): Element | null {
         return document.getElementById(xmlId)
             ?? document.getElementById(this.DecodeXmlIdName(xmlId));
     }

      private DecodeXmlIdName(xmlId: string): string {
          return xmlId.replace(/_x([0-9a-fA-F]{4})_/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
      }
  }

export const designerServiceInstance = createStaticServiceInstance(new DesignerService(), "designerService");
export const useDesignerService = createUseZenAutoArtService(designerServiceInstance);
